<script setup lang="ts">
import { AtomButton, AtomIcon, MolButtonMoney } from "@chatfood/design-system";
import { bemBuilder, scrollToId } from "@chatfood/core-utils";
import { ref, computed, onMounted } from "vue";
import type { IItemComboProps } from "./props";
import type { IItemComboEmits } from "./emits";
import { useBusinessStore } from "~/stores/business";
import {
  ItemHandler,
  type IItemHandlerProps,
  type IItemHandlerEmits,
  type IItemHandlerSelected,
} from "~/components/item-handler";
import { useLocale } from "~/composables/use-locale";

const props = defineProps<IItemComboProps>();
const emits = defineEmits<IItemComboEmits>();

const css = bemBuilder("item-combo");

const { t } = useLocale();

const business = useBusinessStore();

const modifierListSelected = ref<IItemHandlerProps["modifierListSelected"]>([]);
function onModifierListChange(payload: IItemHandlerEmits["change"][0]) {
  modifierListSelected.value = payload.modifierListSelected;
}

onMounted(() => {
  if (!props.itemsSelected?.length) return;

  const parseModifierListSelected = props.itemsSelected.reduce(
    (acc: IItemHandlerProps["modifierListSelected"], item) => {
      const existingItem = acc.find(
        (group) => group.id === item.modifierListId,
      );

      if (existingItem) {
        existingItem.items.push(item);
      } else {
        acc.push({
          id: item.modifierListId,
          items: [item],
        });
      }

      return acc;
    },
    [],
  );

  modifierListSelected.value = parseModifierListSelected;
});

const itemsSelectedTotalPrice = computed(() => {
  return modifierListSelected.value.reduce(
    (acc, cur) => acc + calculateTotalPrice(cur.items),
    0,
  );
});

function calculateTotalPrice(items: IItemHandlerSelected[]): number {
  return items.reduce((total, item) => {
    const sumPrice = item.price.value * item.quantity;
    return total + sumPrice;
  }, 0);
}

const calculatePrice = computed(() => {
  return props.item.price.value + itemsSelectedTotalPrice.value;
});

const invalidModifierLists = ref<string[]>([]);
function handleValidate(payload: IItemHandlerEmits["validate"][0]) {
  invalidModifierLists.value = invalidModifierLists.value.filter(
    (id) => id !== payload.categoryId,
  );

  if (!payload.valid) {
    invalidModifierLists.value.push(payload.categoryId);
  }
}

function onAddCombo() {
  if (invalidModifierLists.value.length) {
    return scrollToId(invalidModifierLists.value[0]);
  }

  emits("change", {
    itemsSelected: modifierListSelected.value.flatMap((obj) => obj.items),
    modifierListId: props.modifierListId,
    itemId: props.item.id,
  });
}

function onClose(event: MouseEvent) {
  emits("close", event);
}
</script>

<template>
  <div :class="css()">
    <div :class="css('header')">
      <AtomButton
        variant="secondary"
        theme="tailored"
        :class="css('header-cta')"
        @click="onClose"
      >
        <AtomIcon name="back" ignore-color />
        {{
          t("components.item_combo.back_button_label", {
            itemName: props.itemName,
          })
        }}
      </AtomButton>
    </div>
    <div :class="css('content')">
      <ItemHandler
        :item="props.item"
        :modifier-list-selected="modifierListSelected"
        @change="onModifierListChange"
        @validate="handleValidate"
      />
    </div>
    <div :class="css('footer')">
      <MolButtonMoney
        :price="{
          value: calculatePrice,
          currency: item.price.currency,
        }"
        :locale="business.locale"
        :hide-cents="false"
        size="large"
        @click.prevent="onAddCombo"
      >
        {{ t("components.item_combo.cta_label") }}
      </MolButtonMoney>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.item-combo {
  display: flex;
  flex: 1;
  overflow: hidden;
  flex-direction: column;

  &__header {
    padding: var(--size-lg);
    border-bottom: 1px solid var(--color-base-90);
  }

  &__header-cta {
    display: flex;
    align-items: center;
    gap: var(--size-sm);
  }

  &__content {
    height: 100%;
    flex: 1;
    padding: var(--size-lg);
    overflow: auto;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    padding: var(--size-lg);
    border-top: 1px solid var(--color-base-90);
  }
}
</style>
